import { Button } from "./ui/Button"
import { UserProfile } from "./ui/UserProfile"
import toast from "react-hot-toast"
import { pushDialog, replaceDialog } from "@/store/dialogs-state"
import { SubscriptionTerm, SubscriptionsDialog } from "./Dialogs/SubscriptionsDialog"
import { useOpenCustomerPortalMutation } from "@/api/payments-api"
import {
	forceRefreshUser,
	registerUserRefreshOnTabReturn,
	useUserActivitiesQuery,
	useUserQuery,
} from "@/api/users-api"
import { activePolarrSubscription, getGenAiCreditsTooltipKey } from "@/api/user-helpers"
import { GetUserResponseBody, PixiesetAccountInfoStatus, UserActivityEvent } from "@/api/dto/users"
import { ConfirmDialog } from "./Dialogs/ConfirmDialog"
import { PNApiOperations } from "@/api/operations"
import { uuid } from "@/utils/uuid"
import { useAuth } from "@/utils/auth"
import CTAText from "./ui/CTAText"
import { VersionInfo } from "./VersionInfo"
import { isGenerativeFillEnabled, isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import { pixiesetURLs } from "@/utils/pixieset-urls"
import { ContentTooltipWrapper } from "./ui/ContentTooltip"
import { Icon } from "./icons/Icon"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { storeCurrentPixiesetSubscriptionSnapshot } from "@/telemetry/subscription-succeeded"
import { getSupportEmail } from "@/utils/support-email"
import { formatNumber } from "@/utils/number"
import {
	formatAccountDate,
	getActivityEventTypeDisplay,
	getGenAiResetDate,
	getLegacySubscriptionTermDisplay,
	getLegacySubscriptionTierDisplay,
} from "./account-localization"

const SubscriptionInfoLine: React.FC<{ title: string; value: string }> = ({ title, value }) => {
	return (
		<div className="flex shrink-0 flex-row flex-nowrap justify-start space-x-3">
			<div className="w-fit shrink-0 flex-nowrap text-sm">{title + ":"}</div>
			<div className="shrink-0 flex-nowrap text-sm text-base-400">{value}</div>
		</div>
	)
}

const GenAiCreditsInfo: React.FC<{ user?: GetUserResponseBody }> = ({ user }) => {
	const { t, i18n } = useTranslation("components")
	const language = i18n.resolvedLanguage ?? i18n.language ?? "en"
	const genAiCreditsRemaining = user?.totalGenAiCreditsAvailable ?? 0
	const genAiResetDate = getGenAiResetDate(user, language)
	const genAiTooltip = t(getGenAiCreditsTooltipKey(user))

	const isGenAIEnabled = isGenerativeFillEnabled()
	if (!isGenAIEnabled) {
		return null
	}
	return (
		<div className="flex flex-col space-y-3">
			<div className="flex shrink-0 flex-row flex-nowrap justify-start space-x-3">
				<div className="w-fit shrink-0 flex-nowrap text-sm">
					<div className="flex flex-row items-center space-x-1">
						<span>{t("account.genAiCredits")}</span>
						<ContentTooltipWrapper
							tooltipDelay={0}
							tooltip={genAiTooltip}
							tooltipProps={{ side: "bottom", sideOffset: 8 }}
						>
							<Icon name="info" size={14} className="text-base-400" />
						</ContentTooltipWrapper>
						<span>:</span>
					</div>
				</div>
				<div className="shrink-0 flex-nowrap text-sm text-base-400">
					{formatNumber(genAiCreditsRemaining)}
				</div>
			</div>
			{genAiResetDate && (
				<SubscriptionInfoLine title={t("account.infoReset")} value={genAiResetDate} />
			)}
		</div>
	)
}

const LegacySubscriptionInfo: React.FC<{ user?: GetUserResponseBody }> = ({ user }) => {
	const { t, i18n } = useTranslation("components")
	const language = i18n.resolvedLanguage ?? i18n.language ?? "en"
	const openCustomerPortal = useOpenCustomerPortalMutation()
	const activeSubscription = activePolarrSubscription(user)

	const subscriptionCredits = () => {
		if (user?.hasUnlimitedExports) {
			return t("account.unlimited")
		}

		let exports = 0
		if (user) {
			for (const subscription of user.subscriptions ?? []) {
				exports += subscription.creditsRemaining
			}

			for (const promo of user.promos ?? []) {
				if (promo.expiresAt) exports += promo.creditsRemaining
			}
		}
		return formatNumber(exports)
	}

	const subscriptionExpiresAt = () => {
		// If there's an active subscription, only show the subscription's expiration time
		if (activeSubscription) {
			return formatAccountDate(activeSubscription.expiresAt, language) ?? t("account.none")
		}

		return t("account.none")
	}

	const purchasedCredits = () => {
		let exports = 0
		if (user) {
			for (const purchase of user.purchases ?? []) {
				exports += purchase.creditsRemaining
			}
			for (const promo of user.promos ?? []) {
				if (!promo.expiresAt) exports += promo.creditsRemaining
			}
		}
		return formatNumber(exports)
	}

	const downloadReceipt = () => {
		openCustomerPortal.mutate()
	}

	const manageSubscription = (term?: SubscriptionTerm) => {
		replaceDialog(SubscriptionsDialog, { initialTerm: term, origin: "account menu" })
	}

	return (
		<div className="flex flex-col space-y-5">
			<div className="flex flex-row justify-between">
				<div className="text-lg">{t("account.subscription")}</div>
				<div className="flex flex-row items-center space-x-1 px-3">
					<div className="text-sm">{t("account.totalCredits")}</div>
					{user?.hasUnlimitedExports && (
						<div className="text-sm text-base-400">{t("account.unlimited")}</div>
					)}
					{!user?.hasUnlimitedExports && (
						<div className="text-sm text-base-400">
							{formatNumber(user?.totalCreditsAvailable)}
						</div>
					)}
				</div>
			</div>
			<div className="grid grid-cols-4 gap-3">
				<div className="flex flex-col space-y-3">
					<SubscriptionInfoLine
						title={t("account.infoSubscriptionTier")}
						value={
							activeSubscription
								? getLegacySubscriptionTierDisplay(activeSubscription.tier, t)
								: t("account.none")
						}
					/>
					<SubscriptionInfoLine
						title={t("account.infoBillingPlan")}
						value={
							activeSubscription
								? `${getLegacySubscriptionTermDisplay(activeSubscription.term, t)}, ${t("account.renewsOn", { date: subscriptionExpiresAt() })}`
								: t("account.none")
						}
					/>
					{activeSubscription && (
						<Button
							trackingLabel="download_receipt"
							aria-label={t("account.ariaDownloadReceipt")}
							className="w-fit p-2"
							size="sm"
							onClick={downloadReceipt}
						>
							{t("account.downloadReceipt")}
						</Button>
					)}
				</div>
				<div className="flex flex-col space-y-3">
					<SubscriptionInfoLine
						title={t("account.infoSubscriptionCredits")}
						value={String(subscriptionCredits())}
					/>
					<SubscriptionInfoLine
						title={t("account.infoExpires")}
						value={subscriptionExpiresAt()}
					/>
					{activeSubscription && (
						<Button
							trackingLabel="manage_subscription"
							aria-label={t("account.ariaManageSubscription")}
							className="w-fit p-2"
							size="sm"
							onClick={() => manageSubscription()}
						>
							{t("account.manageSubscription")}
						</Button>
					)}
				</div>
				<div className="flex flex-col space-y-3">
					<SubscriptionInfoLine
						title={t("account.infoPayAsYouGoCredits")}
						value={String(purchasedCredits())}
					/>
					<div className="text-sm text-base-400">
						{t("account.payAsYouGoDescription")}
					</div>
				</div>
				<GenAiCreditsInfo user={user} />
			</div>
		</div>
	)
}

const SubscriptionInfo: React.FC<{ user?: GetUserResponseBody }> = ({ user }) => {
	const { t } = useTranslation("components")
	const totalCredits = user?.totalCreditsAvailable ?? 0
	const hasPaidSubscription = user?.hasUnlimitedExports === true
	const accountStatus = user?.pixiesetSubscription?.accountInfoStatus
	const billingPlan = user?.pixiesetSubscription?.billingPlan
	const billingPlanDisplay = (() => {
		if (!billingPlan) return t("account.none")
		const normalizedPlan = billingPlan.toLowerCase()
		if (normalizedPlan.includes("monthly")) return t("account.billingTermMonthly")
		if (normalizedPlan.includes("yearly")) return t("account.billingTermYearly")
		return t("account.none")
	})()

	const upgrade = () => {
		replaceDialog(SubscriptionsDialog, { origin: "account menu" })
	}

	const manageSubscription = () => {
		storeCurrentPixiesetSubscriptionSnapshot(user)
		registerUserRefreshOnTabReturn("pixieset-manage-subscription-return")
		window.open(pixiesetURLs.gallery.accountBilling, "_blank", "noopener,noreferrer")
	}

	return (
		<div className="flex flex-col space-y-5">
			<div className="text-lg">{t("account.subscription")}</div>
			<div className="grid grid-cols-3 gap-3">
				<div className="flex flex-col space-y-3">
					<SubscriptionInfoLine
						title={t("account.infoSubscription")}
						value={hasPaidSubscription ? t("account.proPlan") : t("account.none")}
					/>
					<SubscriptionInfoLine
						title={t("account.infoBillingPlan")}
						value={billingPlanDisplay}
					/>
					{accountStatus === PixiesetAccountInfoStatus.Upgraded ? (
						<Button
							trackingLabel="manage_subscription"
							aria-label={t("account.ariaManageSubscription")}
							className="w-fit p-2"
							size="sm"
							onClick={manageSubscription}
						>
							{t("account.manageSubscription")}
						</Button>
					) : (
						<Button
							trackingLabel="upgrade"
							className="w-fit p-2"
							size="sm"
							onClick={upgrade}
						>
							{t("account.upgrade")}
						</Button>
					)}
				</div>
				<div className="flex flex-col space-y-3">
					<SubscriptionInfoLine
						title={t("account.infoExports")}
						value={
							hasPaidSubscription
								? t("account.unlimited")
								: formatNumber(totalCredits)
						}
					/>
				</div>
				<GenAiCreditsInfo user={user} />
			</div>
		</div>
	)
}

const AppInfo: React.FC = () => {
	const { t } = useTranslation("components")
	return (
		<div className="flex flex-col space-y-5">
			<div className="text-lg">{t("account.appInfo")}</div>
			<div className="flex flex-col space-y-3">
				<div className="flex shrink-0 flex-row flex-nowrap justify-start space-x-3">
					<div className="w-fit shrink-0 flex-nowrap text-sm">{t("account.version")}</div>
					<div className="shrink-0 flex-nowrap text-sm text-base-400">
						<VersionInfo />
					</div>
				</div>
			</div>
		</div>
	)
}

const Info: React.FC<{ user: GetUserResponseBody }> = ({ user }) => {
	const { t } = useTranslation("components")
	const { logout } = useAuth()

	if (isPixiesetAuthEnabled()) {
		return (
			<div className="flex flex-row items-start space-x-8">
				<div className="size-[64px] shrink-0">
					<UserProfile user={user} size="medium" />
				</div>
				<div className="flex flex-col items-start space-y-4">
					<div className="text-lg">{user.businessName?.trim() || user.displayName}</div>
					<div className="text-secondary text-sm">{user.email}</div>
					<div className="flex flex-row items-center space-x-3">
						<Button
							trackingLabel="manage_account"
							aria-label={t("account.ariaManagePixiesetAccount")}
							className="w-fit p-2"
							size="sm"
							onClick={() => {
								registerUserRefreshOnTabReturn("pixieset-manage-account-return")
								window.open(
									pixiesetURLs.gallery.account,
									"_blank",
									"noopener,noreferrer",
								)
							}}
						>
							{t("account.manageAccount")}
						</Button>
						<Button
							trackingLabel="log_out"
							aria-label={t("account.ariaLogOut")}
							className="w-fit p-2"
							size="sm"
							onClick={() => void logout()}
						>
							{t("account.logOut")}
						</Button>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="flex flex-row items-start space-x-8">
			<div className="size-[64px] shrink-0">
				<UserProfile user={user} size="medium" />
			</div>
			<div className="flex flex-col items-start space-y-4">
				<div className="text-lg">{user.displayName}</div>
				<div className="flex w-full flex-row items-center space-x-5">
					<div className="text-secondary text-sm">{user.email}</div>
					<Button
						trackingLabel="log_out"
						aria-label={t("account.ariaLogOut")}
						className="w-fit p-2"
						size="sm"
						onClick={() => void logout()}
					>
						{t("account.logOut")}
					</Button>
				</div>
			</div>
		</div>
	)
}

const DeleteAccount: React.FC = () => {
	const { t } = useTranslation("components")
	const { logout } = useAuth()
	const supportEmail = getSupportEmail()

	const deleteAccount = () => {
		pushDialog(ConfirmDialog, {
			title: t("account.confirmDeleteTitle"),
			message: t("account.confirmDeleteMessage"),
			secondary: { title: t("common:action.cancel") },
			primary: {
				title: t("account.deleteAccountButton"),
				isDestructive: true,
				trackingLabel: "delete_account_confirm",
				handler: async () => {
					const deleteAccountResult = await PNApiOperations.deleteCurrentUser()
					if (!deleteAccountResult) {
						toast.error(t("account.deleteFailedToast"))
						return
					}
					void logout()
				},
			},
			trackingViewName: "delete_account_warning",
		})
	}
	return (
		<div className="flex w-full flex-col space-y-5">
			<div className="flex flex-row justify-between">
				<div className="text-lg">{t("account.deleteAccountTitle")}</div>
			</div>
			<div className="text-sm text-base-400">{t("account.deleteAccountWarning")}</div>
			<div className="text-sm text-base-400">
				{t("account.deleteAccountSupport")}{" "}
				<CTAText href={`mailto:${supportEmail}`}>{t("account.contactSupportTeam")}</CTAText>{" "}
				{t("account.deleteAccountSupportSuffix")}
			</div>
			<Button
				className="w-fit p-2"
				trackingLabel="delete_account"
				aria-label={t("account.ariaDeleteAccount")}
				variant="destructive"
				size="sm"
				onClick={deleteAccount}
			>
				{t("account.deleteAccountTitle")}
			</Button>
		</div>
	)
}

export function Account() {
	const { t } = useTranslation("components")
	const { data: user } = useUserQuery()
	const supportEmail = getSupportEmail()

	useEffect(() => {
		void forceRefreshUser()
	}, [])

	if (isPixiesetAuthEnabled()) {
		return (
			<div
				aria-label={t("account.ariaAccount")}
				className="flex size-full flex-col space-y-0"
			>
				<div className="flex size-full flex-1 flex-col space-y-8 overflow-y-scroll bg-base-900 p-8">
					<div className="flex flex-row justify-between text-lg">
						<div className="text-xl font-bold">{t("account.accountTitle")}</div>
					</div>

					{user && <Info user={user} />}

					<div className="flex h-[0.5px] w-full shrink-0 bg-base-800" />

					<SubscriptionInfo user={user} />

					<div className="flex h-[0.5px] w-full shrink-0 bg-base-800" />

					<AppInfo />
				</div>
			</div>
		)
	}

	return (
		<div aria-label={t("account.ariaAccount")} className="flex size-full flex-col space-y-0">
			<div className="flex size-full flex-1 flex-col space-y-8 overflow-y-scroll bg-base-900 p-8">
				<div className="flex flex-row justify-between text-lg">
					<div className="text-xl font-bold">{t("account.accountTitle")}</div>
					<Button
						trackingLabel="contact_support"
						aria-label={t("account.ariaContactSupport")}
						variant="ghost"
					>
						<CTAText href={`mailto:${supportEmail}`}>
							{t("account.contactSupport")}
						</CTAText>
					</Button>
				</div>

				{user && <Info user={user} />}

				<div className="flex h-[0.5px] w-full shrink-0 bg-base-800" />

				<LegacySubscriptionInfo user={user} />

				<div className="flex h-[0.5px] w-full shrink-0 bg-base-800" />

				<AppInfo />

				<div className="flex h-[0.5px] w-full shrink-0 bg-base-800" />

				<DeleteAccount />

				<div className="flex h-[0.5px] w-full shrink-0 bg-base-800" />

				<Activity />
			</div>
		</div>
	)
}

const ActivityLine: React.FC<{ activity: UserActivityEvent }> = ({ activity }) => {
	const { t, i18n } = useTranslation("components")
	const language = i18n.resolvedLanguage ?? i18n.language ?? "en"

	return (
		<div className="grid w-full grid-cols-7 space-x-5 text-sm" key={activity.eventTime}>
			<div>{formatAccountDate(activity.eventTime, language) ?? t("account.none")}</div>
			<div>{getActivityEventTypeDisplay(activity.eventType, t)}</div>
			<div className="col-span-2">{activity.description}</div>
			<div>{formatNumber(activity.amount)}</div>
			<div>
				{activity.hasUnlimitedExports
					? t("account.unlimited")
					: formatNumber(activity.subscriptionBalance)}
			</div>
			<div>{formatNumber(activity.payAsYouGoBalance)}</div>
		</div>
	)
}

const ActivityTitle = () => {
	const { t } = useTranslation("components")
	return (
		<div className="grid w-full grid-cols-7 space-x-5 text-sm text-base-400">
			<div>{t("account.date")}</div>
			<div>{t("account.type")}</div>
			<div className="col-span-2">{t("account.description")}</div>
			<div>{t("account.amount")}</div>
			<div>{t("account.subscriptionCredits")}</div>
			<div>{t("account.paygCredits")}</div>
		</div>
	)
}

const Activity = () => {
	const { t } = useTranslation("components")
	let hasMore: boolean = false
	let activities: UserActivityEvent[] = []
	const activityQuery = useUserActivitiesQuery()

	if (activityQuery.status === "success") {
		activities = activityQuery.data.pages.map((page) => page.data).flat()
		hasMore = activityQuery.data.pages.at(-1)?.hasMore === true
	}

	const loadMoreActivities = () => {
		activityQuery.fetchNextPage()
	}

	return (
		<div className="grow-1 flex w-full flex-col space-y-5">
			<div className="text-lg">{t("account.activities")}</div>
			<div className="flex flex-col space-y-3">
				<ActivityTitle />
				{activities.map((activity) => (
					<ActivityLine activity={activity} key={uuid()} />
				))}
			</div>
			{hasMore && (
				<Button
					trackingLabel="view_more_activities"
					aria-label={t("account.ariaViewMoreAccountActivities")}
					variant="ghost"
					onClick={loadMoreActivities}
				>
					{t("account.viewMoreActivities")}
				</Button>
			)}
		</div>
	)
}
